import { CheckCircleOutline, DeleteOutline } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import debounce from 'lodash/debounce'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import ReactQuill from 'react-quill'
//@ts-ignore
import { decode } from 'html-entities'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import 'react-quill/dist/quill.snow.css'
import { RichTextViewer } from 'src/components/common/RichTextViewer'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { FileType, FormFieldType } from '../../enums'
import { IFile } from '../../generated-types'
import { FormField, getFieldKey, IOption } from '../../generated-types/form-field'
import { AppTextField } from '../AppTextField'
import BasicDatePicker, { DateTimePickerGenericComponent } from '../DateTimePicker'
import { CommonFileUploader, FileUrlDocumentLoader } from '../common/input/CommonFileUploader'
import { ChecklistField } from './ChecklistField'
import { FileUploadField } from './FileUploadField'

interface IFieldSelector {
  field: FormField
  layoutIndex: number
  index: number
  setSelectedSettingField: () => void
  disabled?: boolean
  fieldDisabled?: boolean
  onChange: (values: any) => void
  form?: UseFormReturn
  onDrag?: () => void
  onContentChange?: (field: FormField) => void
  density?: 'default' | 'compact'
  isDraft?: boolean
}

const FieldSelector: React.FC<IFieldSelector> = ({
  field,
  setSelectedSettingField,
  disabled,
  onDrag,
  onChange,
  form,
  fieldDisabled,
  onContentChange,
  density = 'default',
}) => {
  const { t } = useTranslation('overall-reports')
  const theme = useTheme()
  const isCompact = density === 'compact'
  const ptField = isCompact ? 0.35 : 1
  const labelSx = isCompact ? { fontSize: 12, pl: 0, fontWeight: 400 } : undefined
  const values = form?.watch()
  const [isEditing, setIsEditing] = useState(false)
  const [draftContent, setDraftContent] = useState('')

  const debouncedContentChange = useMemo(
    () =>
      debounce(content => {
        onContentChange?.({ ...field, content })
      }, 800),
    [onContentChange, field],
  )

  const onDragStart = useCallback(() => {
    if (onDrag) onDrag()
  }, [])

  const selectField = () => {
    switch (field.type) {
      case FormFieldType.textField:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name}
                <span className="ml-2 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <AppTextField
              type="text"
              sx={{
                paddingLeft: '0px',
              }}
              defaultValue={values && values[getFieldKey(field)]}
              name={getFieldKey(field)}
              fullWidth={true}
              placeholder={field.name}
              disabled={disabled || fieldDisabled}
              onChange={evt => {
                onChange(evt.target.value)
              }}
            />
          </Box>
        )
      case FormFieldType.numberField:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name}
                <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <AppTextField
              type="number"
              sx={{
                paddingLeft: '0px',
              }}
              defaultValue={values && values[getFieldKey(field)]}
              fullWidth={true}
              placeholder={field.name}
              disabled={disabled}
              onChange={evt => {
                onChange(evt.target.value)
              }}
            />
          </Box>
        )
      case FormFieldType.datePicker:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <Box
              sx={{
                marginLeft: '-7px',
              }}
            >
              <BasicDatePicker
                name={getFieldKey(field)}
                isWithoutAppForm={true}
                value={
                  values && values[getFieldKey(field)] ? moment(values[getFieldKey(field)]).format('yyyy-MM-DD') : null
                }
                onChange={value => {
                  onChange(moment(value).format('yyyy-MM-DD'))
                }}
              />
            </Box>
          </Box>
        )
      case FormFieldType.textArea:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <AppTextField
              type="text"
              multiline
              sx={{
                paddingLeft: '0px',
              }}
              rows={isCompact ? 3 : 6}
              fullWidth={true}
              defaultValue={values && values[getFieldKey(field)]}
              placeholder={field.name}
              disabled={disabled}
              onChange={evt => {
                onChange(evt.target.value)
              }}
            />
          </Box>
        )
      case FormFieldType.checkBox:
        return (
          <Box display={'flex'} flexDirection={'row'} draggable={true} onDragStart={onDragStart}>
            <Checkbox
              defaultChecked={values?.[getFieldKey(field)] === 'Yes'}
              disabled={disabled}
              onChange={evt => {
                onChange(evt.target.checked ? 'Yes' : 'No')
              }}
            />
            <Box sx={{ pt: ptField + 1 }}>
              <StyledTypography sx={labelSx}>
                {field.name}
                <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
          </Box>
        )
      case FormFieldType.dateTime:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <Box
              sx={{
                marginLeft: '-7px',
              }}
            >
              <DateTimePickerGenericComponent
                onChange={date => {
                  onChange(date.toISOString())
                }}
                isWithoutAppForm={true}
                name={getFieldKey(field)}
                value={values && values[getFieldKey(field)] ? moment(values[getFieldKey(field)]).toDate() : null}
                fullWidth
              />
            </Box>
          </Box>
        )
      case FormFieldType.fileUpload:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <FileUploadField value={values?.[getFieldKey(field)]} disabled={disabled} onChange={url => onChange(url)} />
          </Box>
        )
      case FormFieldType.timePicker:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <AppTextField
              disabled={disabled}
              type="time"
              fullWidth={true}
              defaultValue={values && values[getFieldKey(field)]}
              placeholder={field.name}
              onChange={evt => {
                onChange(evt.target.value)
              }}
            />
          </Box>
        )
      case FormFieldType.checkBoxList:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            {field.options?.map((option: IOption, index: number) => {
              const savedList: string[] = (() => {
                try {
                  return values?.[getFieldKey(field)] ? JSON.parse(values[getFieldKey(field)]) : []
                } catch {
                  return []
                }
              })()
              return (
                <Box key={index} display={'flex'} flexDirection={'row'}>
                  <Checkbox
                    defaultChecked={savedList.includes(option.value)}
                    disabled={disabled}
                    onChange={evt => {
                      if (form) {
                        const valuesList = form?.getValues()
                        const existingValue = valuesList[getFieldKey(field)]
                        if (existingValue) {
                          const values: any[] = JSON.parse(existingValue)
                          if (evt.target.checked) {
                            values.push(option.value)
                          } else {
                            values.splice(values.indexOf(option.value), 1)
                          }
                          onChange(JSON.stringify(values))
                        } else {
                          onChange(JSON.stringify([option.value]))
                        }
                      }
                    }}
                  />
                  <Box sx={{ pt: ptField }}>
                    <Typography>{option.label}</Typography>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )
      case FormFieldType.radio:
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <Box sx={{ pt: ptField }}>
              <StyledTypography sx={labelSx}>
                {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
              </StyledTypography>
            </Box>
            <RadioGroup
              aria-labelledby={'radioButton' + getFieldKey(field)}
              value={values?.[getFieldKey(field)] ?? ''}
              name={getFieldKey(field)}
              onChange={(evt, value) => {
                onChange(value)
              }}
              sx={isCompact ? { gap: 0, '& .MuiFormControlLabel-root': { my: -0.25 } } : undefined}
            >
              {field.options?.map((option: IOption, index: number) => {
                return (
                  <FormControlLabel
                    key={index}
                    value={option.value}
                    label={option.label}
                    control={<Radio />}
                    disabled={disabled}
                  />
                )
              })}
            </RadioGroup>
          </Box>
        )
      case FormFieldType.selectField:
        return (
          <FormControl fullWidth draggable={true} onDragStart={onDragStart}>
            <InputLabel id={'selectedFieldLabel' + getFieldKey(field)}>
              {field.name} <span className="ml-1 text-red-500">{field.isRequired && '*'}</span>
            </InputLabel>
            <Select
              fullWidth={true}
              labelId={'selectedFieldLabel' + getFieldKey(field)}
              id={`select-field-${getFieldKey(field)}`}
              label={field.name}
              value={values?.[getFieldKey(field)] ?? ''}
              onChange={evt => {
                onChange(evt.target.value)
              }}
            >
              {field.options?.map((option: IOption, index: number) => {
                return (
                  <MenuItem key={index} value={option.value}>
                    {option.label}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        )
      case FormFieldType.title:
        return (
          <Typography
            variant={isCompact ? 'subtitle2' : 'subtitle1'}
            fontWeight={600}
            sx={isCompact ? { mt: 0, mb: 0.25, color: 'text.primary' } : { mt: 1, mb: 0.5, color: 'text.primary' }}
            draggable={true}
            onDragStart={onDragStart}
          >
            {field.name}
          </Typography>
        )
      case FormFieldType.richText:
        if (disabled) {
          // Builder mode: click to open inline rich text editor
          return isEditing ? (
            <Box
              onClick={e => {
                e.stopPropagation()
                setSelectedSettingField()
              }}
              sx={{
                '& .ql-container': { minHeight: 150, maxHeight: 300 },
                '& .ql-editor': { maxHeight: 300, fontWeight: 400 },
                '& .ql-editor strong': { fontWeight: 700 },
              }}
            >
              <ReactQuill
                theme="snow"
                value={draftContent}
                onChange={content => {
                  setDraftContent(content)
                  debouncedContentChange(content)
                }}
              />
              <Box display="flex" justifyContent="flex-end">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setIsEditing(false)
                  }}
                >
                  <CheckCircleOutline />
                </IconButton>
              </Box>
            </Box>
          ) : (
            // Fill mode: Show rich text content or placeholder
            <Box
              draggable={true}
              onDragStart={onDragStart}
              onClick={e => {
                e.stopPropagation()
                setSelectedSettingField()
                setDraftContent(field.content || '')
                setIsEditing(true)
              }}
              border={`1px dashed ${getThemeColor(theme, MUIThemeColors.secondaryMain)}`}
              padding={1}
              minHeight={60}
              sx={{ cursor: 'pointer' }}
            >
              {field.content ? (
                <RichTextViewer html={decode(field.content ?? '')} maxHeight={300} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('click-to-add-richText', { ns: 'form-schema' })}
                </Typography>
              )}
            </Box>
          )
        }

        return <RichTextViewer html={decode(field.content ?? '')} maxHeight={400} />
      case FormFieldType.instructionImage:
        if (!disabled) {
          // Preview mode: show static image read-only
          return field.content ? (
            <Box>
              <img
                src={field.content}
                alt="instruction"
                style={{ width: `${field.imageWidth ?? 100}%`, borderRadius: 4, display: 'block' }}
              />
            </Box>
          ) : null
        }
        // Builder mode: if content exists show preview and remove button
        if (field.content) {
          return (
            <Box draggable={true} onDragStart={onDragStart}>
              <Box sx={{ position: 'relative', display: 'inline-block', width: `${field.imageWidth ?? 100}%` }}>
                <img
                  src={field.content}
                  alt="instruction"
                  style={{ width: '100%', borderRadius: 4, display: 'block' }}
                />
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation()
                    onContentChange?.({ ...field, content: '' })
                  }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: `${getThemeColor(theme, MUIThemeColors.white)}`,
                    '&:hover': { bgcolor: `${getThemeColor(theme, MUIThemeColors.white)}80` },
                  }}
                >
                  <DeleteOutline fontSize="small" color="error" />
                </IconButton>
              </Box>
            </Box>
          )
        }
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <CommonFileUploader
              name={field.identifier || field.name}
              type={FileType.Image}
              acceptedFileTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
              userMessage={t('image-upload-user-message', { ns: 'common' })}
              multiple={false}
              disabled={false}
              isDeletable={false}
              height="120px"
              onChange={file => {
                onContentChange?.({ ...field, content: (file as IFile).url })
              }}
              showFileTypes={false}
              iconHeight={'40px'}
              iconWidth={'40px'}
            />
          </Box>
        )
      case FormFieldType.instructionFile:
        if (!disabled) {
          // Preview mode: show static file link read-only
          return field.content ? (
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <FileUrlDocumentLoader fileUrl={field.content} openOnClick={true} />
              <Typography variant="body2">{field.name}</Typography>
            </Box>
          ) : null
        }
        // Builder mode: if content exists show preview with remove button, else show uploader
        if (field.content) {
          return (
            <Box
              draggable={true}
              onDragStart={onDragStart}
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap={1}
              border="1px solid #e0e0e0"
              borderRadius={1}
              padding={1}
              bgcolor="#fafafa"
            >
              <FileUrlDocumentLoader fileUrl={field.content} openOnClick={true} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {field.name}
              </Typography>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  onContentChange?.({ ...field, content: '', name: '' })
                }}
              >
                <DeleteOutline fontSize="small" color="error" />
              </IconButton>
            </Box>
          )
        }
        return (
          <Box draggable={true} onDragStart={onDragStart}>
            <CommonFileUploader
              name={field.identifier || field.name}
              multiple={false}
              disabled={false}
              isDeletable={false}
              height="120px"
              onChange={file => {
                const f = file as IFile
                onContentChange?.({ ...field, content: f.url, name: f.filename })
              }}
              showFileTypes={false}
              iconHeight={'40px'}
              iconWidth={'40px'}
            />
          </Box>
        )
      case FormFieldType.checklist:
        return (
          <ChecklistField
            field={field}
            disabled={disabled}
            form={form}
            onDragStart={onDragStart}
            onChange={onChange}
            setSelectedSettingField={setSelectedSettingField}
          />
        )
      default:
        return null
    }
  }
  return (
    <Box
      sx={{
        pl: isCompact ? 0.25 : '10px',
        pr: isCompact ? 0.25 : 0,
      }}
      onClick={() => {
        setSelectedSettingField()
      }}
    >
      {selectField()}
    </Box>
  )
}

const StyledTypography = styled(Typography)({
  fontSize: 13,
  paddingLeft: '10px',
})

export default FieldSelector
